import React from 'react';
import { useSchedulesQuery } from '../queries';
import { useScheduleStatus, type SchedulePhase, type ScheduleStatus } from '../hooks/useScheduleStatus';
import type { Event } from '../types';

export type ScheduleContextValue = {
  currentScheduleId: number | string | null;
  setCurrentScheduleId: (scheduleId: number | string | null) => void;
  scheduleStatus: ScheduleStatus;
  /** @deprecated Use scheduleStatus.nextEvent */
  nextEvent: any | null;
  /** @deprecated Use scheduleStatus.eventId */
  eventId: number | string | null;
  /** @deprecated Use scheduleStatus.eventTimestamp */
  eventTimestamp: number | null;
  /** @deprecated Use scheduleStatus.endTimestamp */
  endTimestamp: number | null;
  /** @deprecated Use scheduleStatus.phase === 'live' */
  isLive: boolean;
  /** @deprecated Use scheduleStatus.countdownSeconds */
  countdownSeconds: number | null;
  /** @deprecated Use scheduleStatus.ongoingPayload */
  ongoingPayload: Record<string, unknown> | null;
  refetchOngoing: () => void;
  sportId: number | string | null;
  /** @deprecated Use scheduleStatus.phase !== 'idle' && scheduleStatus.phase !== 'countdown' */
  eventStarted: boolean;
  /** @deprecated Use scheduleStatus.phase === 'starting' || scheduleStatus.phase === 'live' */
  inLiveWindowByTime: boolean;
  selectedEvent: Event | null;
  setSelectedEvent: (event: Event | null) => void;
  selectedWeekId: number | null;
  setSelectedWeekId: (weekId: number | null) => void;
};

const noopRefetch = () => {};
const defaultStatus: ScheduleStatus = {
  phase: 'idle' as SchedulePhase,
  eventId: null,
  eventTimestamp: null,
  countdownSeconds: null,
  endTimestamp: null,
  ongoingPayload: null,
  vodReady: false,
  nextEvent: null,
  refetchOngoing: noopRefetch,
  notEndedEvents: [],
};

const defaultValue: ScheduleContextValue = {
  currentScheduleId: null,
  setCurrentScheduleId: () => {},
  scheduleStatus: defaultStatus,
  nextEvent: null,
  eventId: null,
  eventTimestamp: null,
  endTimestamp: null,
  isLive: false,
  countdownSeconds: null,
  ongoingPayload: null,
  refetchOngoing: noopRefetch,
  sportId: null,
  eventStarted: false,
  inLiveWindowByTime: false,
  selectedEvent: null,
  setSelectedEvent: () => {},
  selectedWeekId: null,
  setSelectedWeekId: () => {},
};

const ScheduleContext = React.createContext<ScheduleContextValue>(defaultValue);

export function useScheduleContext(): ScheduleContextValue {
  const ctx = React.useContext(ScheduleContext);
  if (ctx == null) {
    return defaultValue;
  }
  return ctx;
}

type ScheduleProviderProps = {
  children: React.ReactNode;
};

export function ScheduleProvider({ children }: ScheduleProviderProps) {
  const [currentScheduleId, setCurrentScheduleId] = React.useState<number | string | null>(null);
  const [selectedEvent, setSelectedEvent] = React.useState<Event | null>(null);
  const [selectedWeekId, setSelectedWeekId] = React.useState<number | null>(null);

  const setCurrentScheduleIdWrapped = React.useCallback((id: number | string | null) => {
    setCurrentScheduleId(id);
  }, []);

  const { data: schedules = [] } = useSchedulesQuery();
  const currentSchedule = React.useMemo(
    () =>
      Array.isArray(schedules)
        ? schedules.find((s: { schedule_id?: unknown }) => String(s.schedule_id) === String(currentScheduleId))
        : null,
    [schedules, currentScheduleId]
  );
  const sportId =
    (currentSchedule as { sport?: { sport_id?: number }; sport_id?: number })?.sport?.sport_id ??
    (currentSchedule as { sport_id?: number })?.sport_id ??
    null;

  const status = useScheduleStatus(currentScheduleId, sportId ?? undefined);

  const eventStarted = status.phase === 'starting' || status.phase === 'live' || status.phase === 'ended';
  const inLiveWindowByTime = status.phase === 'starting' || status.phase === 'live';

  // Selection sync: when schedule changes clear selection
  const prevScheduleIdRef = React.useRef(currentScheduleId);
  React.useEffect(() => {
    if (prevScheduleIdRef.current !== currentScheduleId) {
      prevScheduleIdRef.current = currentScheduleId;
      setSelectedEvent(null);
      setSelectedWeekId(null);
    }
  }, [currentScheduleId]);

  React.useEffect(() => {
    const nextEvent = status.nextEvent;
    const notEndedEvents = status.notEndedEvents ?? [];
    const nextEventId = status.eventId != null ? String(status.eventId) : null;

    if (selectedEvent == null) {
      if (nextEvent != null && nextEventId != null) {
        setSelectedEvent(nextEvent as Event);
      }
      return;
    }

    const selectedId = selectedEvent?.event_id != null ? String(selectedEvent.event_id) : null;
    const stillInList =
      selectedId != null &&
      notEndedEvents.some((e: { event_id?: unknown }) => String(e?.event_id ?? '') === selectedId);

    if (stillInList) {
      return;
    }
  }, [
    selectedEvent?.event_id,
    status.nextEvent,
    status.eventId,
    status.notEndedEvents,
  ]);

  const value: ScheduleContextValue = {
    currentScheduleId,
    setCurrentScheduleId: setCurrentScheduleIdWrapped,
    scheduleStatus: status,
    nextEvent: status.nextEvent,
    eventId: status.eventId,
    eventTimestamp: status.eventTimestamp,
    endTimestamp: status.endTimestamp,
    isLive: status.phase === 'live',
    countdownSeconds: status.countdownSeconds,
    ongoingPayload: status.ongoingPayload,
    refetchOngoing: status.refetchOngoing,
    sportId,
    eventStarted,
    inLiveWindowByTime,
    selectedEvent,
    setSelectedEvent,
    selectedWeekId,
    setSelectedWeekId,
  };

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
}
